import React, { useCallback, useContext, useRef } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { UI } from "@wwimmo/ui";
import { AGENDAITEM_PROPERTY, ProtocolTagKeys } from "src/stores/assemblies/AgendaItemStore";
import styles from "./ProtocolText.module.css";
import { RootStoreContext } from "src/stores/RootStore";
import RichTextEditor from "src/components/RichTextEditor/RichTextEditor";
import { Editor, EditorEvents } from "@tiptap/react";
import DOMPurify from "dompurify";
import { getHtmlStringWithLineBreaks } from "src/utils/HTMLUtils";

const ProtocolTextBase = observer(() => {
    const { agendaitemStore, uiStore } = useContext(RootStoreContext);
    const { t } = useTranslation();
    const richTextEditorRef = useRef<Editor | null>(null);

    const onRichTextEditorCreated = useCallback((editor: EditorEvents["create"]) => {
        richTextEditorRef.current = editor.editor;
    }, []);

    const onClickProtocolTag = useCallback(
        (translationKey: string) => (e: any) => {
            if (agendaitemStore.currentAgendaItem) {
                // Insert the tag at the end of the document
                richTextEditorRef.current
                    ?.chain()
                    .command(({ tr: transcation }) => {
                        transcation.insertText(
                            t(translationKey),
                            richTextEditorRef.current?.state.doc.content.size || 0
                        );
                        return true;
                    })
                    .run();

                // Set the cursor to the end of the document
                richTextEditorRef.current
                    ?.chain()
                    .focus()
                    .setTextSelection(richTextEditorRef.current?.state.doc.content.size)
                    .run();
            }
        },
        [agendaitemStore.currentAgendaItem, t]
    );

    const onBlurRichTextEditorField = useCallback(
        (editor: EditorEvents["blur"]) => {
            agendaitemStore.saveProtocolText();
        },
        [agendaitemStore]
    );

    const onChangeRichTextEditorField = useCallback(
        (update: EditorEvents["update"]) => {
            if (update.editor.getText().length === 0) {
                // If the editor content is getting emptied we want to remove the empty paragraph tag
                agendaitemStore.updateAgendaItem(AGENDAITEM_PROPERTY.PROTOCOLTEXT, "");
            } else {
                agendaitemStore.updateAgendaItem(
                    AGENDAITEM_PROPERTY.PROTOCOLTEXT,
                    getHtmlStringWithLineBreaks(DOMPurify.sanitize(update.editor.getHTML()))
                );
            }
            agendaitemStore.setAgendaItemsHaveChanged();
        },
        [agendaitemStore]
    );

    return (
        <div className={styles.ProtocolContainer}>
            {!agendaitemStore.isAgendaItemLocked && (
                <div className={styles.ProtocolTags}>
                    {Object.values(ProtocolTagKeys).map((key) => (
                        <div key={key} className={styles.Tags} onClick={onClickProtocolTag(key)}>
                            {t(key)} <UI.Icon icon={UI.SVGIcon.Add} size={12} className={styles.AddIconTags} />
                        </div>
                    ))}
                </div>
            )}
            <RichTextEditor
                enableContentCheck={true}
                stickyToolbar={true}
                content={getHtmlStringWithLineBreaks(
                    DOMPurify.sanitize(agendaitemStore.currentAgendaItem?.protocoltext || "")
                )}
                placeholder={
                    agendaitemStore.isAgendaItemLocked
                        ? ""
                        : t("screens.assemblies.agenda.protocol_placeholder").toString()
                }
                onContentUpdate={onChangeRichTextEditorField}
                onEditorCreate={onRichTextEditorCreated}
                onBlur={onBlurRichTextEditorField}
                disabled={agendaitemStore.isAgendaItemLocked}
                stickyToolbarOffsetOverride={uiStore.headerHeight - 9}
            />
        </div>
    );
});

export const ProtocolText = ProtocolTextBase;
